import React, { useEffect, useMemo } from 'react';
import { CircularProgress, Stack, Typography } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import { useGeneratePreferredAnswerRequirementsLazyQuery } from 'generated-graphql/hooks/apollo-hooks';
import { PreferredAnswerRequirementsList } from 'common/ui';

type ScorecardFormData = {
  content: string;
  preferredAnswerText?: string;
};

type Props = {
  initialValues: ScorecardFormData;
  onRequirementsGenerated?: (requirements: string[]) => void;
};

export const QualifyingQuestionScorecard: React.FC<Props> = ({
  initialValues,
  onRequirementsGenerated
}) => {
  const [fetchRequirements, { loading, error, data }] =
    useGeneratePreferredAnswerRequirementsLazyQuery();

  // Memoize the values we care about to prevent unnecessary effect triggers
  const memoizedValues = useMemo(
    () => ({
      content: initialValues.content,
      preferredAnswerText: initialValues.preferredAnswerText
    }),
    [initialValues.content, initialValues.preferredAnswerText]
  );

  useEffect(() => {
    if (!memoizedValues.content || !memoizedValues.preferredAnswerText) {
      return;
    }

    fetchRequirements({
      variables: {
        question: memoizedValues.content,
        preferredAnswer: memoizedValues.preferredAnswerText
      }
    });
  }, [memoizedValues, fetchRequirements]);

  useEffect(() => {
    if (
      data?.generatePreferredAnswerRequirements?.statements &&
      onRequirementsGenerated
    ) {
      onRequirementsGenerated(
        data.generatePreferredAnswerRequirements.statements
      );
    }
  }, [data]);

  return (
    <>
      {loading && (
        <Stack direction="row" gap={1} alignItems="center">
          <CircularProgress
            size={20}
            sx={{
              color: 'customGrey.main'
            }}
          />
          <Typography>Generating</Typography>
        </Stack>
      )}
      {!loading &&
        !!data?.generatePreferredAnswerRequirements?.statements?.length && (
          <PreferredAnswerRequirementsList
            requirements={data.generatePreferredAnswerRequirements.statements}
          />
        )}
      {!loading && error && (
        <Stack direction="row" gap={1} alignItems="center">
          <ErrorIcon fontSize="small" color="error" />
          <Typography>
            Could not generate requirements. Try a different answer or question.
          </Typography>
        </Stack>
      )}
    </>
  );
};
